import type { TenantId } from '../../config/runtime/types';
import { normalizeTradeStatus, resolveTradeRealizedPnl, type AnyTrade } from './authoritativeTradeData';
import { isTradeWithinTenantDisplayScope } from './tenantTradeScope';

export function hasCanonicalDashboardTrades(trades: unknown): boolean {
  return Array.isArray(trades) && trades.some(isCanonicalDashboardTrade);
}

export function isCanonicalDashboardTrade(trade: unknown): trade is Record<string, unknown> {
  if (!trade || typeof trade !== 'object') return false;
  const row = trade as Record<string, unknown>;

  if (row.rejected === true || row.isRejected === true || row.quarantined === true || row.isQuarantined === true) return false;
  if (row.accepted === false || row.valid === false || row.isValid === false) return false;
  if (row.validationStatus === 'rejected' || row.validationStatus === 'quarantined' || row.importStatus === 'rejected' || row.importStatus === 'quarantined') return false;
  if (row.rejectionReason != null || row.rejectionReasons != null || row.quarantineReason != null) return false;

  return true;
}

/**
 * The shared analytics/display source. It rejects invalid import rows and
 * applies the active tenant's product asset policy before any user-facing
 * surface can derive metrics from the records.
 */
export function filterCanonicalDisplayTrades<T>(trades: T[], tenantId?: TenantId): T[] {
  if (!Array.isArray(trades)) return [];
  return trades.filter((trade) => (
    isCanonicalDashboardTrade(trade) && isTradeWithinTenantDisplayScope(trade, tenantId)
  )) as T[];
}

export function filterCanonicalRealizedTrades<T>(trades: T[], tenantId?: TenantId): T[] {
  return filterCanonicalDisplayTrades(trades, tenantId).filter((trade) => {
    const canonical = trade as unknown as AnyTrade;
    return normalizeTradeStatus(canonical) === 'closed'
      && resolveTradeRealizedPnl(canonical) != null;
  });
}

// Retained for existing dashboard callers. New display surfaces should use
// the tenant-neutral name above.
export function filterCanonicalDashboardTrades<T>(trades: T[], tenantId?: TenantId): T[] {
  return filterCanonicalDisplayTrades(trades, tenantId);
}

export function buildDashboardTradeSourceSignature(trades: unknown): string {
  const canonicalTrades = filterCanonicalDashboardTrades(Array.isArray(trades) ? trades : []);
  if (canonicalTrades.length === 0) return 'empty';

  return canonicalTrades
    .map((trade, index) => {
      if (!trade || typeof trade !== 'object') return `invalid:${index}`;
      const row = trade as Record<string, unknown>;
      return [
        row.storageId,
        row.persistedTradeId,
        row.id,
        row.symbol,
        row.openTimestampMs ?? row.entryTimestamp ?? row.entryTime ?? row.timestamp ?? row.date,
        row.closeTimestampMs ?? row.exitTimestamp ?? row.exitTime ?? row.closedAt,
        row.pnl ?? row.calculatedPnl ?? row.brokerReportedPnl,
        row.status,
      ].map((part) => String(part ?? '')).join(':');
    })
    .join('|');
}
